import React, { type CSSProperties } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { CrmOportunidadListItem } from '../../../types/crm';
import { formatCurrency } from '../../../utils/formatters';

interface CrmDealCardProps {
  deal: CrmOportunidadListItem;
  onClick?: () => void;
  dragDisabled?: boolean;
}

const CrmDealCard: React.FC<CrmDealCardProps> = ({ deal, onClick, dragDisabled = false }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `deal-${deal.id}`,
    data: { deal },
    disabled: dragDisabled,
  });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    cursor: dragDisabled ? 'pointer' : 'grab',
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      sx={{
        p: 1.5,
        mb: 1,
        bgcolor: '#fff',
        borderRadius: 2,
        border: '1px solid',
        borderColor: deal.estancada ? 'warning.main' : 'divider',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' },
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {deal.nombre}
          </Typography>
          {deal.estancada && <Chip size="small" label="Estancada" color="warning" />}
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {formatCurrency(deal.monto)}
        </Typography>
        {deal.contactoPrincipal && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {deal.contactoPrincipal}
          </Typography>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary" noWrap>
            {deal.nombreVendedor || `Vend. ${deal.nroVendedor}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {deal.diasEnEtapa}d
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CrmDealCard;
